import type { Game, Standing, Sport, ChatRequest } from "../types";

// API response shape from the backend
interface ApiToolUsed {
  toolName: string;
  result: string;
}

export interface ApiChatResponse {
  message: string;
  sessionId: string;
  timestamp: string;
  toolsUsed: ApiToolUsed[] | null;
  tokenUsage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
}

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : "/api/v1";

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "An error occurred" }));
    throw new ApiError(
      response.status,
      error.message || `HTTP ${response.status}`,
    );
  }

  return response.json();
}

// Sports data endpoints
export const sportsApi = {
  getLiveScores: (sport?: Sport): Promise<Game[]> =>
    sport ? fetchApi(`/games/live/${sport}`) : fetchApi("/games/live"),

  getGamesByDate: (sport: Sport, date: string): Promise<Game[]> =>
    fetchApi(`/games/${sport}/date/${date}`),

  getUpcomingGames: (sport: Sport, limit = 10): Promise<Game[]> =>
    fetchApi(`/games/${sport}/upcoming?limit=${limit}`),

  getStandings: (sport: Sport): Promise<Standing[]> =>
    fetchApi(`/standings/${sport}`),

  getGame: (gameId: string): Promise<Game> => fetchApi(`/games/${gameId}`),
};

// Chat endpoints
export const chatApi = {
  sendMessage: (request: ChatRequest): Promise<ApiChatResponse> =>
    fetchApi("/chat", {
      method: "POST",
      body: JSON.stringify(request),
    }),

  getSessions: (): Promise<{ sessions: string[]; count: number }> =>
    fetchApi("/chat/sessions"),

  getSessionHistory: (sessionId: string): Promise<ApiChatResponse[]> =>
    fetchApi(`/chat/sessions/${sessionId}/history`),
};

// Admin API types
export interface JobSyncLog {
  id: string;
  providerName: string;
  sport: string;
  dataType: string;
  lastSyncAttempt: string | null;
  lastSuccessfulSync: string | null;
  lastStatus:
    | "SUCCESS"
    | "PARTIAL"
    | "FAILED"
    | "RATE_LIMITED"
    | "TIMEOUT"
    | null;
  lastErrorMessage: string | null;
  consecutiveFailures: number | null;
  totalSyncsToday: number | null;
  totalFailuresToday: number | null;
  lastSyncDurationMs: number | null;
  recordsProcessed: number | null;
  recordsCreated: number | null;
  recordsUpdated: number | null;
  isActive: boolean | null;
}

export interface JobHealthSummary {
  totalJobs: number;
  healthyJobs: number;
  failingJobs: number;
  rateLimitedJobs: number;
  totalSyncsToday: number;
  totalFailuresToday: number;
  successRate: number;
  timestamp: string;
}

export interface DatabaseStats {
  totalGames: number;
  totalOdds: number;
  totalStandings: number;
  totalSyncLogs: number;
  liveGames: number;
  gamesBySport: Record<string, number>;
  timestamp: string;
}

// Sync result type
export interface SyncResult {
  success: boolean;
  provider: string | null;
  sport: string;
  dataType: string;
  processed: number;
  created: number;
  updated: number;
  durationMs: number;
  message: string | null;
}

// Job configuration types
export interface JobConfig {
  sport: string;
  sportDisplayName: string;
  dataType: string;
  dataTypeDescription: string;
  provider: string;
  hasRun: boolean;
  lastStatus:
    | "SUCCESS"
    | "PARTIAL"
    | "FAILED"
    | "RATE_LIMITED"
    | "TIMEOUT"
    | null;
  lastSuccessfulSync: string | null;
  healthy: boolean;
}

export interface JobConfigMatrix {
  jobs: JobConfig[];
  supportedBySport: Record<string, string[]>;
  totalConfigs: number;
  configsWithHistory: number;
  timestamp: string;
}

// Admin endpoints
export const adminApi = {
  getJobHealth: (): Promise<JobHealthSummary> => fetchApi("/admin/jobs/health"),

  getJobConfigs: (): Promise<JobConfigMatrix> => fetchApi("/admin/jobs/config"),

  getAllJobs: (): Promise<JobSyncLog[]> => fetchApi("/admin/jobs"),

  getActiveJobs: (): Promise<JobSyncLog[]> => fetchApi("/admin/jobs/active"),

  getFailingJobs: (minFailures = 1): Promise<JobSyncLog[]> =>
    fetchApi(`/admin/jobs/failing?minFailures=${minFailures}`),

  getJobsByStatus: (status: string): Promise<JobSyncLog[]> =>
    fetchApi(`/admin/jobs/by-status/${status}`),

  getJobsBySport: (sport: string): Promise<JobSyncLog[]> =>
    fetchApi(`/admin/jobs/by-sport/${sport}`),

  getDatabaseStats: (): Promise<DatabaseStats> =>
    fetchApi("/admin/database/stats"),

  // Sync triggers
  triggerLiveSync: (sport: string): Promise<SyncResult> =>
    fetchApi(`/admin/sync/${sport}/live`, { method: "POST" }),

  triggerScheduleSync: (sport: string, date?: string): Promise<SyncResult> =>
    fetchApi(`/admin/sync/${sport}/schedule${date ? `?date=${date}` : ""}`, {
      method: "POST",
    }),

  triggerStandingsSync: (sport: string, season?: string): Promise<SyncResult> =>
    fetchApi(
      `/admin/sync/${sport}/standings${season ? `?season=${season}` : ""}`,
      { method: "POST" },
    ),

  triggerOddsSync: (sport: string): Promise<SyncResult> =>
    fetchApi(`/admin/sync/${sport}/odds`, { method: "POST" }),

  triggerSyncAll: (): Promise<{
    liveScores: SyncResult[];
    schedules: SyncResult[];
  }> => fetchApi("/admin/sync/all", { method: "POST" }),
};

// Voice API types
export interface TranscribeResponse {
  success: boolean;
  text: string | null;
  error: string | null;
}

export interface VoiceStatusResponse {
  configured: boolean;
  transcription: boolean;
  synthesis: boolean;
}

export interface VoicesResponse {
  voices: string[];
  default: string;
}

// Voice endpoints
export const voiceApi = {
  /** Check if voice service is configured */
  getStatus: (): Promise<VoiceStatusResponse> => fetchApi("/voice/status"),

  /** Get available TTS voices */
  getVoices: (): Promise<VoicesResponse> => fetchApi("/voice/voices"),

  /** Transcribe audio to text */
  transcribe: async (audioBlob: Blob, hint?: string): Promise<string> => {
    const formData = new FormData();

    // Determine file extension from mime type
    const extension = audioBlob.type.includes("webm")
      ? "webm"
      : audioBlob.type.includes("mp4")
        ? "mp4"
        : "webm";
    formData.append("file", audioBlob, `audio.${extension}`);

    if (hint) {
      formData.append("hint", hint);
    }

    const response = await fetch(`${API_BASE}/voice/transcribe`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: "Transcription failed" }));
      throw new ApiError(
        response.status,
        error.error || "Transcription failed",
      );
    }

    const data: TranscribeResponse = await response.json();
    if (!data.success || !data.text) {
      throw new ApiError(500, data.error || "Transcription failed");
    }

    return data.text;
  },

  /** Synthesize text to speech */
  synthesize: async (text: string, voice: string = "echo"): Promise<Blob> => {
    const response = await fetch(`${API_BASE}/voice/synthesize`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, voice }),
    });

    if (!response.ok) {
      throw new ApiError(response.status, "Speech synthesis failed");
    }

    return response.blob();
  },
};

export { ApiError };
